import { Action, createReducer, on } from '@ngrx/store';
import { IPMessage } from '../../models/pmessage.model';
import { ILoaderActionPayload, LoadingState } from '../models/loader.model';
import { EntityMediaItem, EntityMediaResponse, UserItem, UserItemMinimal, UserListResponse } from '../../../api/models';
import { usersActions } from './users.actions';
import { UserControllerFind$Params } from '../../../api/fn/user/user-controller-find';

export interface UsersState {
  list: {
    state: ILoaderActionPayload;
    data?: UserListResponse;
    request?: UserControllerFind$Params;
  };
  single: {
    state: ILoaderActionPayload;
    data?: UserItem;
  };
  singleMedia: {
    state: ILoaderActionPayload;
    data?: EntityMediaResponse;
    skip?: number;
    limit?: number;
  };
  deleteOneMedia: {
    state: ILoaderActionPayload;
    data?: EntityMediaItem;
  };
  saveOne: { state: ILoaderActionPayload; data?: UserItem };
  createOne: { state: ILoaderActionPayload; data?: UserItem };
  uploadAvatar: { state: ILoaderActionPayload; data?: UserItem };
  moveToTrash: { state: ILoaderActionPayload; data?: UserItemMinimal };
  trashList: { state: ILoaderActionPayload; data?: UserListResponse };
  restoreFromTrash: { state: ILoaderActionPayload; data?: UserItemMinimal };
  forceDelete: { state: ILoaderActionPayload; data?: UserItemMinimal };
  updateCredentials: { state: ILoaderActionPayload };
}

const initialState: UsersState = {
  list: { state: { value: LoadingState.NONE } },
  single: { state: { value: LoadingState.NONE } },
  saveOne: { state: { value: LoadingState.NONE } },
  createOne: { state: { value: LoadingState.NONE } },
  uploadAvatar: { state: { value: LoadingState.NONE } },
  moveToTrash: { state: { value: LoadingState.NONE } },
  trashList: { state: { value: LoadingState.NONE } },
  restoreFromTrash: { state: { value: LoadingState.NONE } },
  forceDelete: { state: { value: LoadingState.NONE } },
  singleMedia: { state: { value: LoadingState.NONE } },
  deleteOneMedia: { state: { value: LoadingState.NONE } },
  updateCredentials: { state: { value: LoadingState.NONE } },
};

const reducer = createReducer(
  initialState,
  // load
  on(usersActions.load, (state) => ({
    ...state,
    list: { state: { value: LoadingState.LOADING } },
  })),
  on(usersActions.done, (state, props) => ({
    ...state,
    list: { state: props.state, data: props.data, request: props.request },
  })),
  // loadOne
  on(usersActions.loadOne, (state) => ({
    ...state,
    single: { state: { value: LoadingState.LOADING } },
  })),
  on(usersActions.doneOne, (state, props) => ({
    ...state,
    single: { state: props.state, data: props.data },
  })),
  on(usersActions.loadOneSetState, (state, props) => ({
    ...state,
    single: { state: { value: props.state.value, message: undefined } },
  })),
  // loadOneMedia
  on(usersActions.loadOneMedia, (state, props) => ({
    ...state,
    singleMedia: { limit: props.limit, skip: props.skip, state: { value: LoadingState.LOADING } },
  })),
  on(usersActions.loadOneMediaDone, (state, props) => ({
    ...state,
    singleMedia: { state: props.state, data: props.data },
  })),
  on(usersActions.loadOneMediaSetState, (state, props) => ({
    ...state,
    singleMedia: { state: { value: props.state.value, message: undefined } },
  })),
  // deleteOneMedia
  on(usersActions.deleteOneMedia, (state, props) => ({
    ...state,
    deleteOneMedia: { state: { value: LoadingState.LOADING } },
  })),
  on(usersActions.deleteOneMediaDone, (state, props) => ({
    ...state,
    deleteOneMedia: { state: props.state, data: props.data },
  })),
  on(usersActions.deleteOneMediaSetState, (state, props) => ({
    ...state,
    deleteOneMedia: { state: { value: props.state.value, message: undefined } },
  })),
  // saveOne
  on(usersActions.saveOne, (state) => ({
    ...state,
    saveOne: { state: { value: LoadingState.LOADING } },
  })),
  on(usersActions.saveOneDone, (state, props) => ({
    ...state,
    saveOne: { state: props.state, data: props.data },
    single: state.single
      ? { state: { value: LoadingState.SUCCESS }, data: props.data }
      : { state: { value: LoadingState.NONE } },
  })),
  on(usersActions.saveOneSetState, (state, props) => ({
    ...state,
    saveOne: { state: { value: props.state.value, message: undefined } },
  })),
  // createOne
  on(usersActions.createOne, (state) => ({
    ...state,
    createOne: { state: { value: LoadingState.LOADING } },
  })),
  on(usersActions.createOneDone, (state, props) => ({
    ...state,
    createOne: { state: props.state, data: props.data },
  })),
  on(usersActions.createOneSetState, (state, props) => ({
    ...state,
    createOne: { state: { value: props.state.value, message: undefined } },
  })),
  // uploadAvatar
  on(usersActions.uploadAvatar, (state) => ({
    ...state,
    uploadAvatar: { state: { value: LoadingState.LOADING } },
  })),
  on(usersActions.uploadAvatarDone, (state, props) => ({
    ...state,
    uploadAvatar: { state: props.state, data: props.data },
    single: state.single
      ? { state: { value: LoadingState.SUCCESS }, data: props.data }
      : { state: { value: LoadingState.NONE } },
  })),
  on(usersActions.uploadAvatarFail, (state, props) => ({
    ...state,
    uploadAvatar: { state: props.state },
  })),
  on(usersActions.uploadAvatarSetState, (state, props) => ({
    ...state,
    uploadAvatar: { state: { value: props.state.value, message: props.state.message ?? undefined } },
  })),
  // move to trash
  on(usersActions.moveToTrash, (state) => ({
    ...state,
    moveToTrash: { state: { value: LoadingState.LOADING } },
  })),
  on(usersActions.moveToTrashDone, (state, props) => ({
    ...state,
    moveToTrash: { state: props.state, data: props.data },
    single: { state: { value: LoadingState.NONE } },
  })),
  on(usersActions.moveToTrashSetState, (state, props) => ({
    ...state,
    moveToTrash: { state: { value: props.state.value, message: undefined } },
  })),

  // restore from trash
  on(usersActions.restoreFromTrash, (state) => ({
    ...state,
    restoreFromTrash: { state: { value: LoadingState.LOADING } },
  })),
  on(usersActions.restoreFromTrashDone, (state, props) => ({
    ...state,
    restoreFromTrash: { state: props.state, data: props.data },
    single: { state: { value: LoadingState.NONE } },
  })),
  on(usersActions.restoreFromTrashSetState, (state, props) => ({
    ...state,
    restoreFromTrash: { state: { value: props.state.value, message: undefined } },
  })),

  // forced delete
  on(usersActions.forceDelete, (state) => ({
    ...state,
    forceDelete: { state: { value: LoadingState.LOADING } },
  })),
  on(usersActions.forceDeleteDone, (state, props) => ({
    ...state,
    forceDelete: { state: props.state, data: props.data },
    single: { state: { value: LoadingState.NONE } },
  })),
  on(usersActions.forceDeleteSetState, (state, props) => ({
    ...state,
    forceDelete: { state: { value: props.state.value, message: undefined } },
  })),

  // trashList
  on(usersActions.trashList, (state) => ({
    ...state,
    trashList: { state: { value: LoadingState.LOADING } },
  })),
  on(usersActions.trashListDone, (state, props) => ({
    ...state,
    trashList: { state: props.state, data: props.data },
  })),
  on(usersActions.trashListSetState, (state, props) => ({
    ...state,
    trashList: { state: { value: props.state.value, message: undefined } },
  })),

  // updateCredential
  on(usersActions.updateCredentials, (state) => ({
    ...state,
    updateCredentials: { state: { value: LoadingState.LOADING } },
  })),
  on(usersActions.updateCredentialsDone, (state, props) => ({
    ...state,
    updateCredentials: { state: props.state },
  })),
  on(usersActions.updateCredentialsSetState, (state, props) => ({
    ...state,
    updateCredentials: { state: { value: props.state.value, message: undefined } },
  })),
);

export function usersReducer(state: UsersState | undefined, action: Action) {
  return reducer(state, action);
}
